import { useState, useCallback } from 'react';
import { Block, DropTarget } from '../types';

export const useBlockDragDrop = () => {
  const [isDragging, setIsDragging] = useState(false);
  const [draggedBlocks, setDraggedBlocks] = useState<string[]>([]);
  const [dropTarget, setDropTarget] = useState<DropTarget | null>(null);

  // Commencer le drag
  const startDrag = useCallback((blockId: string, selectedBlocks: Set<string>, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const blocksToDrag = selectedBlocks.has(blockId)
      ? Array.from(selectedBlocks)
      : [blockId];

    setIsDragging(true);
    setDraggedBlocks(blocksToDrag);
  }, []);

  // Mettre à jour la position de drop pendant le drag
  const updateDragPosition = useCallback((
    e: MouseEvent,
    editorRect: DOMRect,
    blocks: Block[],
    _rootBlocks: string[]
  ) => {
    if (!isDragging) return;

    const mouseY = e.clientY - editorRect.top;

    let closestIndex = -1;
    let closestDistance = Infinity;
    let targetParentId: string | undefined;
    let targetLevel = 0;
    let dropPosition: 'above' | 'below' | 'on' = 'below';
    let indicatorTop = 0;

    blocks.forEach((block, index) => {
      const blockElement = document.querySelector(`[data-block-id="${block.id}"]`) as HTMLElement;
      if (!blockElement) return;

      const rect = blockElement.getBoundingClientRect();
      const blockY = rect.top - editorRect.top;
      const blockCenter = blockY + rect.height / 2;
      const verticalDistance = Math.abs(mouseY - blockCenter);

      if (verticalDistance < closestDistance) {
        closestDistance = verticalDistance;
        closestIndex = index;

        const blockLevel = block.level;
        const relativeY = mouseY - blockY;
        const threshold = rect.height * 0.3;

        if (relativeY < threshold) {
          // Haut du bloc → drop AU-DESSUS (même parent)
          dropPosition = 'above';
          targetLevel = blockLevel;
          targetParentId = block.parentId;
          indicatorTop = blockY;
        } else if (relativeY > rect.height - threshold) {
          // Bas du bloc → sibling APRÈS (même parent)
          dropPosition = 'below';
          targetLevel = blockLevel;
          targetParentId = block.parentId;
          indicatorTop = blockY + rect.height;
        } else {
          // Milieu du bloc → toujours enfant de la cible (Notion style)
          dropPosition = 'on';
          targetLevel = blockLevel + 1;
          targetParentId = block.id;
          indicatorTop = blockY + rect.height;
        }
      }
    });

    if (closestIndex >= 0) {
      setDropTarget({
        index: closestIndex,
        parentId: targetParentId,
        level: targetLevel,
        position: dropPosition,
        top: indicatorTop
      });
    }
  }, [isDragging]);

  // Terminer le drag
  const endDrag = useCallback(() => {
    setIsDragging(false);
    setDraggedBlocks([]);
    setDropTarget(null);
  }, []);

  // Vérifier si un bloc est en cours de drag
  const isBlockDragging = useCallback((blockId: string) => {
    return isDragging && draggedBlocks.includes(blockId);
  }, [isDragging, draggedBlocks]);

  return {
    isDragging,
    draggedBlocks,
    dropTarget,
    startDrag,
    updateDragPosition,
    endDrag,
    isBlockDragging
  };
};
